import { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE = 10;

export interface Pagination<T> {
  /** Zero-based current page index (clamped into range). */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  pageSize: number;
  /** Total items in the full list (pre-slice). */
  total: number;
  /** The items for the current page only. */
  pageItems: T[];
  /** 1-based index of the first item on the page (0 when the list is empty). */
  from: number;
  /** 1-based index of the last item on the page. */
  to: number;
  canPrev: boolean;
  canNext: boolean;
  next: () => void;
  prev: () => void;
  setPage: (p: number) => void;
}

/**
 * Client-side "Top N + paginate" over an already-fetched array.
 *
 * Default page size is 10 — the house rule is "show the top 10, then
 * pagination after that". Pair with the shared <Pager> control, which
 * self-hides when there is only one page so small lists stay clean.
 *
 * Auto-resets to page 0 when the underlying list shrinks (e.g. a filter
 * narrows the results) so you never sit on an out-of-range empty page.
 */
export function usePagination<T>(
  items: T[],
  pageSize: number = DEFAULT_PAGE_SIZE,
): Pagination<T> {
  const [page, setPage] = useState(0);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [pageCount, page]);

  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * pageSize;
  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  );

  return {
    page: safePage,
    pageCount,
    pageSize,
    total,
    pageItems,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
    canPrev: safePage > 0,
    canNext: safePage < pageCount - 1,
    next: () => setPage((p) => Math.min(p + 1, pageCount - 1)),
    prev: () => setPage((p) => Math.max(p - 1, 0)),
    setPage,
  };
}
